<script lang="ts">
	import type { PanoramaBlock } from '$models/Block/PanoramaBlock';
	import Button from '$components/UI/Buttons/Button.svelte';
	import { getPanoramaImageSources } from '$lib/panorama';
	import { sanitizeHtml } from '$lib/utils';
	import { onMount } from 'svelte';

	// Component properties
	export let block: PanoramaBlock;
	export let isPriority = false;

	const imageSources = getPanoramaImageSources(block);
	const TICKER_ANIMATION_MS = 2000;

	let activeTickerIndex = 0;
	let animateTicker = true;

	onMount(() => {
		animateTicker =
			!window.matchMedia('(prefers-reduced-motion: reduce)').matches && block.keywords.length > 1;
	});

	function handleTickerAnimationEnd() {
		if (!animateTicker || block.keywords.length <= 1) {
			return;
		}

		activeTickerIndex = (activeTickerIndex + 1) % block.keywords.length;
	}
</script>

<div class="panorama">
	<div class="panorama-container panorama-container--align-{block.textAlignment} container">
		<div class="panorama-content panorama-content--text-{block.textColor}">
			{#if block.tagline || block.keywords.length}
				<h1 class="panorama-title">
					{#if block.tagline}
						<span class="panorama-title-static">
							{@html sanitizeHtml(block.tagline)}
						</span>
					{/if}

					{#if block.keywords.length}
						<span class="panorama-title-ticker" aria-live="polite">
							{#key activeTickerIndex}
								<span
									class="panorama-title-ticker-word"
									class:panorama-title-ticker-word--animated={animateTicker}
									style="--ticker-duration: {TICKER_ANIMATION_MS}ms"
									onanimationend={handleTickerAnimationEnd}
								>
									{block.keywords[activeTickerIndex]}
								</span>
							{/key}
						</span>
					{/if}
				</h1>
			{/if}

			{#if block.text}
				<div class="panorama-text">
					{@html sanitizeHtml(block.text)}
				</div>
			{/if}
			{#if block.button}
				<div class="panorama-button">
					<Button type="anchor" href={block.button.url} variant={block.buttonClass}>
						{block.button.title}
					</Button>
				</div>
			{/if}
		</div>
	</div>
	<figure class="panorama-image">
		{#if block.imageOverlay && block.imageOverlay > 0}
			<div class="panorama-overlay" style="opacity: {block.imageOverlay / 100}"></div>
		{/if}
		<picture>
			{#each imageSources.slice(0, -1) as { key, media, image } (key)}
				<source {media} srcset={image.url} />
			{/each}
			<img
				src={imageSources.at(-1)?.image.url ?? block.imageMobile.url}
				alt={block.imageDesktop.alt}
				width={block.imageMobile.width}
				height={block.imageMobile.height}
				loading={isPriority ? 'eager' : 'lazy'}
				fetchpriority={isPriority ? 'high' : 'auto'}
				decoding="async"
			/>
		</picture>
	</figure>
</div>

<style lang="postcss">
	.panorama {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 480px;
		overflow: hidden;

		@media (--sm) {
			height: 580px;
		}

		@media (--xl) {
			height: 640px;
		}

		&-container {
			position: relative;
			z-index: 2;
			width: 100%;
			display: flex;
			justify-content: center;
			text-align: center;
			max-width: var(--container);
			padding-left: var(--spacing-5);
			padding-right: var(--spacing-5);

			&--align-left {
				text-align: left;
				justify-content: flex-start;
			}

			&--align-center {
				text-align: center;
				justify-content: center;
			}

			&--align-right {
				text-align: right;
				justify-content: flex-end;
			}
		}

		&-content {
			color: var(--color-white);
			max-width: var(--panorama-container-width-sm);

			@media (--sm) {
				max-width: var(--panorama-container-width);
			}

			&--text-dark {
				color: var(--color-brown);
			}
		}

		&-title {
			font-size: var(--panorama-title-size-sm);
			font-family: var(--panorama-title-family);
			text-transform: var(--panorama-title-transform);
			font-weight: var(--panorama-title-weight);
			line-height: 1;
			margin-bottom: var(--spacing-6);

			@media (--sm) {
				font-size: var(--panorama-title-size-md);
			}

			@media (--md) {
				font-size: var(--panorama-title-size-lg);
			}
		}

		&-title-static {
			display: block;
		}

		&-title-ticker {
			display: block;
			overflow: hidden;
			height: var(--panorama-title-size-sm);

			@media (--sm) {
				height: var(--panorama-title-size-md);
			}

			@media (--md) {
				height: var(--panorama-title-size-lg);
			}
		}

		&-title-ticker-word {
			display: block;
			height: var(--panorama-title-size-sm);

			@media (--sm) {
				height: var(--panorama-title-size-md);
			}

			@media (--md) {
				height: var(--panorama-title-size-lg);
			}

			&--animated {
				animation: panorama-ticker-slide var(--ticker-duration, 1s) ease-in-out forwards;
			}
		}

		&-text {
			margin-bottom: var(--spacing-6);

			@media (--sm) {
				font-size: var(--panorama-text-size);
			}
		}

		&-image {
			position: absolute;
			z-index: 1;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			width: 100%;
			height: 100%;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		&-overlay {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			width: 100%;
			height: 100%;
			z-index: 2;
			background-color: var(--color-brown);
		}
	}

	@keyframes panorama-ticker-slide {
		0% {
			transform: translateY(100%);
		}

		20% {
			transform: translateY(0);
		}

		75% {
			transform: translateY(0);
		}

		100% {
			transform: translateY(100%);
		}
	}
</style>
